<!-- ExcerptMultiSelectMiniModal.vue -->

<script setup>
import { computed, watch } from "vue";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },
  excerpts: { type: Array, default: () => [] },
  title: { type: String, default: "Trechos selecionados" },
  // Criador da atividade atual — necessário para a regra "somente conteúdo do titular".
  activityCreatedByUserId: { type: Number, default: null },
});

const emit = defineEmits(["close", "removeExcerpt", "bulkAdd", "clearExcerpts"]);

const hasExcerpts = computed(
  () => Array.isArray(props.excerpts) && props.excerpts.length > 0
);

// 🔹 NOVO: arrastar o painel pelo cabeçalho. O componente fica montado e só
// alterna `show`, então o watch abaixo recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// Mesmo gate do item "Criar notas em lote" no menu de ações
// (NoteActionsButton.vue): sem permissão de criar nota na atividade, o botão
// continua na tela, esmaecido e inerte, com o motivo no tooltip.
const { canCreateNoteInActivity } = useMemberRestrictions();

const canBulkAdd = computed(() =>
  canCreateNoteInActivity(props.activityCreatedByUserId)
);

// ✅ LIFO: mais recente em cima (inverte a ordem)
const reversedExcerpts = computed(() =>
  Array.isArray(props.excerpts) ? [...props.excerpts].reverse() : []
);

// Offsets do trecho no texto. Aceita os mesmos apelidos tolerados por
// BulkAddNoteForm.normalizedExcerpts, para o painel não depender de quem montou
// o objeto.
function getRange(excerpt) {
  const start =
    excerpt?.start ?? excerpt?.offsetStart ?? excerpt?.textRangeStart ?? null;
  const end =
    excerpt?.end ?? excerpt?.offsetEnd ?? excerpt?.textRangeEnd ?? null;

  if (!Number.isFinite(Number(start)) || !Number.isFinite(Number(end))) {
    return null;
  }
  return { start: Number(start), end: Number(end) };
}

function formatRange(excerpt) {
  const range = getRange(excerpt);
  if (!range) return "";
  return `${range.start}–${range.end}`;
}

function handleClose() {
  emit("close");
}

function handleRemoveExcerpt(excerpt) {
  // Emite o próprio objeto; o pai decide como remover
  emit("removeExcerpt", excerpt);
}

function handleBulkAdd() {
  if (!hasExcerpts.value || !canBulkAdd.value) return;
  emit("bulkAdd");
}

// Esvazia a pilha de trechos SEM sair do modo de seleção múltipla — é o que
// diferencia do [×] do cabeçalho, que limpa e encerra o modo.
function handleClearExcerpts() {
  if (!hasExcerpts.value) return;
  emit("clearExcerpts");
}
</script>

<template>
  <Teleport to="body">
    <transition name="excerpt-mini-fade">
      <div
        v-if="show"
        class="excerpt-mini-backdrop"
        aria-modal="true"
        role="dialog"
        aria-label="Trechos selecionados"
      >
        <div
          ref="panelRef"
          class="excerpt-mini-modal"
          :style="panelStyle"
          @click.stop
        >
          <!-- Cabeçalho -->
          <div
            class="excerpt-mini-header"
            :class="{
 'excerpt-mini-header--draggable': isDraggable,
              'excerpt-mini-header--dragging': isDragging,
            }"
            @pointerdown="onHandlePointerDown"
          >
            <div class="excerpt-mini-title">
              <i class="fas fa-i-cursor mr-1.5"></i>

              <!-- TEXTO + CONTADOR -->
              <span>
                {{ title }}
                <span v-if="hasExcerpts" class="excerpt-count">
                  • {{ excerpts.length }}
                </span>
              </span>
            </div>

            <!-- ✅ Botão de fechar no padrão do AddAuthorForm.vue -->
            <button
              type="button"
              class="modal-close-btn"
              @click="handleClose"
              aria-label="Fechar painel de trechos"
            >
              <!-- ✅ ÍCONE MENOR (controlado por CSS, sem Tailwind) -->
              <svg
                class="modal-close-icon"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>

          <!-- Conteúdo -->
          <div class="excerpt-mini-body">
            <p v-if="!hasExcerpts" class="excerpt-empty">
              Nenhum trecho selecionado ainda. Ative o modo de seleção múltipla
              e arraste o cursor sobre partes do texto para empilhá-las aqui.
            </p>

            <ul v-else class="excerpt-list">
              <li
                v-for="(excerpt, index) in reversedExcerpts"
                :key="index"
                class="excerpt-item"
              >
                <div class="excerpt-index">{{ index + 1 }}</div>

                <div class="excerpt-cell">
                  <div class="excerpt-text" :title="excerpt.text">
                    {{ excerpt.text }}
                  </div>

                  <!-- Range de caracteres do trecho no texto -->
                  <div
                    v-if="formatRange(excerpt)"
                    class="excerpt-range"
                    title="Intervalo de caracteres no texto"
                  >
                    <i
                      class="fas fa-location-crosshairs range-icon"
                      aria-hidden="true"
                    ></i>
                    <span>{{ formatRange(excerpt) }}</span>
                  </div>
                </div>

                <!-- ✅ Botão apagar no mesmo estilo do AddPermissionForm.vue -->
                <button
                  type="button"
                  class="btn-icon btn-icon-delete excerpt-delete-btn"
                  @click="handleRemoveExcerpt(excerpt)"
                  title="Apagar trecho"
                  aria-label="Apagar trecho selecionado"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </li>
            </ul>
          </div>

          <!-- Rodapé fixo: atalho para a mesma ação do menu "Ações → Criar
               notas em lote". Fica fora da área rolável para continuar
               alcançável quando a lista de trechos passa da altura visível. -->
          <div v-if="hasExcerpts" class="excerpt-mini-footer">
            <button
              type="button"
              class="excerpt-footer-btn excerpt-clear-btn"
              @click="handleClearExcerpts"
              title="Remover todos os trechos selecionados"
            >
              <span>Limpar</span>
            </button>

            <button
              type="button"
              class="excerpt-footer-btn excerpt-bulk-add-btn"
              :class="{ 'excerpt-btn--blocked': !canBulkAdd }"
              :disabled="!canBulkAdd"
              :title="
                canBulkAdd
                  ? null
                  : 'Você só pode anotar em atividades preparadas pelo titular ou pelo círculo dele.'
              "
              @click="handleBulkAdd"
            >
              <!-- Mesmo ícone do botão "Criar nova nota" (AddNoteForm.vue) -->
              <svg
                class="excerpt-bulk-add-icon"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Criar notas em lote</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* ====== TRANSIÇÃO ====== */
.excerpt-mini-fade-enter-active,
.excerpt-mini-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.excerpt-mini-fade-enter-from,
.excerpt-mini-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* ====== BACKDROP ====== */
.excerpt-mini-backdrop {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9998;

  display: flex;
  justify-content: flex-end;
  align-items: flex-end;

  padding: 0 16px 18px 16px;
}

/* ====== MINI-MODAL ====== */
.excerpt-mini-modal {
  pointer-events: auto;
  background-color: var(--surface);
  border-radius: 14px;
  border: 1px solid var(--border-color);
  box-shadow: 0 14px 35px rgba(0, 0, 0, 0.15);
  width: 360px;
  /* 260px do corpo/cabeçalho + ~44px do rodapé fixo */
  max-height: 304px;

  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-size: 0.86rem;
}

/* Cabeçalho */
.excerpt-mini-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 4px;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--brand-soft) 0%, #f5f3ff 40%, #ffffff 100%);
}

/* 🔹 NOVO: cabeçalho como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.excerpt-mini-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.excerpt-mini-header--dragging {
  cursor: grabbing;
}
/* O botão de fechar continua sendo clique, não alça */
.excerpt-mini-header--draggable .modal-close-btn {
  cursor: pointer;
}

.excerpt-mini-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: var(--text-on-brand);
}

.excerpt-mini-title i {
  color: #7c3aed;
  font-size: 0.9rem;
}

/* ✅ Ícone menor SEM Tailwind */
.modal-close-icon {
  width: 14px;
  height: 14px;
  display: block;
}

/* Corpo */
.excerpt-mini-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px 10px 10px;
  overflow-y: auto;
  max-height: 220px;
}

/* Rodapé fixo — só a lista de trechos rola; os botões permanecem visíveis */
.excerpt-mini-footer {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-top: 1px solid var(--border-color);
  background-color: var(--surface-2);
}

.excerpt-footer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  height: 30px;
  padding: 0 0.8rem;

  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-on-brand);

  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.excerpt-footer-btn:active {
  transform: translateY(1px);
}

/* Secundário, largura pelo conteúdo (padrão "Cancelar" dos demais rodapés) */
.excerpt-clear-btn {
  flex: 0 0 auto;
  background-color: var(--neutral-200);
  border-color: var(--neutral-400);
  color: var(--text-dark);
}

.excerpt-clear-btn:hover {
  background-color: var(--neutral-300);
}

/* Ação principal — ocupa o espaço restante */
.excerpt-bulk-add-btn {
  flex: 1 1 auto;
  background-color: var(--brand);
}

.excerpt-bulk-add-btn:hover {
  background-color: var(--brand-hover);
}

/* Bloqueado pelo vínculo: visível, esmaecido e sem resposta ao hover. */
.excerpt-btn--blocked,
.excerpt-btn--blocked:hover {
  opacity: 0.45;
  cursor: not-allowed;
  background-color: var(--brand);
}

.excerpt-bulk-add-icon {
  width: 16px;
  height: 16px;
  display: block;
  flex-shrink: 0;
}

.excerpt-empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.3;
}

.excerpt-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.excerpt-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto; /* índice | texto | ícone */
  gap: 6px;
  align-items: center;
  padding: 6px 7px;
  border-radius: 10px;
  background-color: var(--surface-2);
  border: 1px solid var(--border-color);
}

.excerpt-item:hover {
  background-color: #f3f4ff;
  border-color: #c7d2fe;
}

.excerpt-index {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background-color: var(--brand-soft);
  color: var(--text-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

/* Coluna central: texto (recortado) + range logo abaixo, sempre visível */
.excerpt-cell {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.excerpt-text {
  font-size: 0.82rem;
  color: var(--text-dark);
  line-height: 1.35;
  max-height: 3.3em;
  overflow: hidden;
  text-overflow: ellipsis;
}

.excerpt-range {
  display: flex;
  align-items: center;
  gap: 0.3rem;

  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.6rem;
  font-style: italic;
  line-height: 1;
  color: var(--text-muted);
  letter-spacing: 0.01em;
}

/* Mesma convenção do AiNoteGeneratorForm.vue */
.range-icon {
  color: #c4b5fd;
  font-size: 0.58rem;
  font-style: normal;
  flex-shrink: 0;
}

.btn-icon {
  width: 24px;
}

.btn-icon-delete {
  background-color: var(--danger-bg);
  color: var(--danger-text);
}

/* ajuste fino na célula do botão */
.excerpt-delete-btn {
  flex-shrink: 0;
}
.excerpt-count {
  margin-left: 4px;
  font-weight: 600;
  color: var(--text-muted); /* cinza elegante */
}
</style>
